import React, { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import MainHeader from '../header/MainHeader';
import Footer from '../footer/Footer';
import { Link, useParams } from 'react-router-dom';
import './detail.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendar,
  faMotorcycle,
  faBolt,
  faVanShuttle,
  faTruck,
  faRotateLeft,
  faSliders,
  faBus,
  faTractor,
  faGears,
  faTruckRampBox,
} from '@fortawesome/free-solid-svg-icons';
import { useListingDetails } from '../listing/hooks/useListingDetails';
import { favoritesService, listingsService } from '../api';
import { getFeatureLabel } from '../utils/featureMapper';
import { formatPrice } from '../mysearch/mysearchConstants';

function SellerDetail() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();

  const { loading, listing, error, startingChat, handleStartChat } =
    useListingDetails(id);

  // Favorites state
  const [isFavorite, setIsFavorite] = useState(false);
  const [togglingFavorite, setTogglingFavorite] = useState(false);

  // Similar ads state
  const [similarListings, setSimilarListings] = useState<any[]>([]);
  const [loadingSimilar, setLoadingSimilar] = useState(false);

  // Check if listing is favorite on mount
  useEffect(() => {
    if (id) {
      const checkFavorite = async () => {
        try {
          const favorite = await favoritesService.isFavorite(id);
          setIsFavorite(favorite);
        } catch (error) {
          console.error('Error checking favorite status:', error);
        }
      };
      checkFavorite();
    }
  }, [id]);

  // Fetch similar listings when listing is loaded
  useEffect(() => {
    const fetchSimilarListings = async () => {
      if (!listing || !listing.make || !listing.model) return;

      setLoadingSimilar(true);
      try {
        // First, get the full listing details to access category
        let category: string | undefined;
        try {
          const listingResponse = await listingsService.getById(id!);
          const apiData = (listingResponse.data || listingResponse) as any;
          category = apiData.category;
        } catch (err) {
          console.warn('Could not fetch category from listing:', err);
        }

        // Build query params with category, make, and model filters
        const params: any = {
          page: 1,
          limit: 4, // Limit to 4 similar ads
          make: listing.make,
          model: listing.model,
          category: listing.category,
        };

        // Add category if available
        if (category) {
          params.category = category;
        }

        const response = await listingsService.getAll(params);

        if (response && response.listings) {
          // Filter out the current listing and limit to 4
          const filtered = response.listings
            .filter((item: any) => item.id !== listing.id)
            .slice(0, 4);
          setSimilarListings(filtered);
        }
      } catch (error) {
        console.error('Error fetching similar listings:', error);
        setSimilarListings([]);
      } finally {
        setLoadingSimilar(false);
      }
    };

    if (listing && id) {
      fetchSimilarListings();
    }
  }, [listing, id]);

  // Toggle favorite
  const handleToggleFavorite = async () => {
    if (!id || togglingFavorite) return;

    setTogglingFavorite(true);
    try {
      if (isFavorite) {
        await favoritesService.removeFavorite(id);
        setIsFavorite(false);
      } else {
        await favoritesService.addFavorite(id);
        setIsFavorite(true);
      }
    } catch (error: any) {
      console.error('Error toggling favorite:', error);
      alert(error.message || t('seller_detail.failed_favorite'));
    } finally {
      setTogglingFavorite(false);
    }
  };

  const images = useMemo(() => {
    if (listing?.images && listing.images.length > 0) {
      return listing.images.map(img =>
        typeof img === 'string'
          ? img
          : (img as any).url ||
            (img as any).originalUrl ||
            '/images/placeholder.jpg'
      );
    }

    // Fallback to static demo images
    return [
      '/images/seller_1.webp',
      '/images/seller_2.webp',
      '/images/seller_3.webp',
      '/images/seller_1.webp',
      '/images/seller_2.webp',
    ];
  }, [listing]);

  const [activeIndex, setActiveIndex] = useState(0);

  const nextImage = () => {
    setActiveIndex(prev => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setActiveIndex(prev => (prev - 1 + images.length) % images.length);
  };

  if (loading) {
    return (
      <>
        <MainHeader />
        <div className="sellerdetail-slider-section">
          <div className="container text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">{t('seller_detail.loading')}</span>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (error || !listing) {
    return (
      <>
        <MainHeader />
        <div className="sellerdetail-slider-section">
          <div className="container text-center py-5">
            <h4 className="mb-3">{t('seller_detail.not_found')}</h4>
            <p className="text-muted">
              {t('seller_detail.not_found_message')}
            </p>
            <Link to="/" className="btn btn-custom mt-3">
              {t('seller_detail.back_home')}
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <MainHeader />

      {/* ===== Hero Section ===== */}
      <div className="sellerdetail-section">
        <div className="mysearch-content">
          <h1>{t('seller_detail.hero_title')}</h1>
          <nav className="breadcrumb">
            <Link to="/">
              <i className="fa fa-home me-1"></i> {t('seller_detail.breadcrumb_home')}
            </Link>
            <span className="mx-2">
              <i className="fa fa-chevron-right"></i>
            </span>
            <span>{t('seller_detail.breadcrumb_current')}</span>
          </nav>
        </div>
      </div>

      {/* ===== Slider Section ===== */}
      <div className="sellerdetail-slider-section">
        <div className="container slider-container">
          <div className="d-flex gap-2 justify-content-end mb-4">
            <button
              className={`btn btn-custom-outline w-auto px-3 ${isFavorite ? 'favorited' : ''}`}
              onClick={handleToggleFavorite}
              disabled={togglingFavorite}
              style={{
                color: isFavorite ? '#ff4757' : 'inherit',
              }}
            >
              {togglingFavorite ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  {isFavorite ? t('seller_detail.removing') : t('seller_detail.saving')}
                </>
              ) : (
                <>
                  <i
                    className={`fa-${isFavorite ? 'solid' : 'regular'} fa-heart me-2`}
                  ></i>
                  {isFavorite ? t('seller_detail.saved') : t('seller_detail.save')}
                </>
              )}
            </button>
            <button className="btn btn-custom-outline w-auto">
              <i className="fas fa-share me-2"></i> {t('seller_detail.share')}
            </button>
          </div>
          <div className="row">
            <div className="col-lg-8">
              <div className="sticky-sidebar">
                <div className="main-image-area">
                  <img
                    src={images[activeIndex]}
                    className="main-image"
                    alt="car"
                  />

                  {/* Left Arrow */}
                  <button className="arrow-btn left" onClick={prevImage}>
                    ❮
                  </button>

                  {/* Right Arrow */}
                  <button className="arrow-btn right" onClick={nextImage}>
                    ❯
                  </button>
                </div>
                <div className="thumbnail-row">
                  {images.map((img, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => setActiveIndex(index)}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setActiveIndex(index);
                        }
                      }}
                      className={`thumb ${index === activeIndex ? 'active' : ''}`}
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        cursor: 'pointer',
                      }}
                    >
                      <img
                        src={img}
                        alt="thumb"
                        style={{
                          display: 'block',
                          width: '100%',
                          height: '100%',
                        }}
                      />
                    </button>
                  ))}
                </div>
              </div>
              <div className="sticky-sidebar mt-3">
                <div className="seller-category-grid text-center">
                  {/* Year */}
                  <div className="outer_card">
                    <FontAwesomeIcon icon={faCalendar} className="icon_track" />
                    <p className="mt-2 mb-0 fw-semibold text-dark">
                      {listing.year || 'N/A'}
                    </p>
                  </div>

                  {/* Mileage */}
                  <div className="outer_card">
                    <FontAwesomeIcon icon={faRotateLeft} className="icon_track" />
                    <p className="mt-2 mb-0 fw-semibold text-dark">
                      {listing.mileage?.toLocaleString() || 0} km
                    </p>
                  </div>

                  {/* Fuel Type */}
                  <div className="outer_card">
                    <FontAwesomeIcon icon={faBolt} className="icon_track" />
                    <p className="mt-2 mb-0 fw-semibold text-dark">
                      {listing.fuelType || 'N/A'}
                    </p>
                  </div>

                  {/* Transmission */}
                  <div className="outer_card">
                    <FontAwesomeIcon icon={faGears} className="icon_track" />
                    <p className="mt-2 mb-0 fw-semibold text-dark">
                      {listing.transmission || 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
              <div className="sticky-sidebar mt-3">
                <div className="seller-description-box">
                  <h4 className="mb-3">{t('seller_detail.technical_data')}</h4>
                  <table className="table table-striped table-hover">
                    <tbody>
                      <tr>
                        <th scope="row">{t('seller_detail.vehicle')}</th>
                        <td>{listing.year} {listing.make} {listing.model}</td>
                      </tr>
                      {listing.mileage != null && (
                        <tr>
                          <th scope="row">{t('seller_detail.mileage', 'Mileage')}</th>
                          <td>{listing.mileage.toLocaleString()} km</td>
                        </tr>
                      )}
                      {listing.fuelType && (
                        <tr>
                          <th scope="row">{t('seller_detail.fuel_type', 'Fuel Type')}</th>
                          <td>{listing.fuelType}</td>
                        </tr>
                      )}
                      {listing.transmission && (
                        <tr>
                          <th scope="row">{t('seller_detail.transmission', 'Transmission')}</th>
                          <td>{listing.transmission}</td>
                        </tr>
                      )}
                      {listing.bodyType && (
                        <tr>
                          <th scope="row">{t('seller_detail.body_type', 'Body Type')}</th>
                          <td>{listing.bodyType}</td>
                        </tr>
                      )}
                      {listing.condition && (
                        <tr>
                          <th scope="row">{t('seller_detail.condition', 'Condition')}</th>
                          <td>{listing.condition}</td>
                        </tr>
                      )}
                      {listing.color && (
                        <tr>
                          <th scope="row">{t('seller_detail.color', 'Color')}</th>
                          <td>{listing.color}</td>
                        </tr>
                      )}
                      {listing.doors != null && (
                        <tr>
                          <th scope="row">{t('seller_detail.doors', 'Doors')}</th>
                          <td>{listing.doors}</td>
                        </tr>
                      )}
                      {listing.seats != null && (
                        <tr>
                          <th scope="row">{t('seller_detail.seats', 'Seats')}</th>
                          <td>{listing.seats}</td>
                        </tr>
                      )}
                      {listing.country && (
                        <tr>
                          <th scope="row">{t('seller_detail.country', 'Country')}</th>
                          <td>{listing.country}</td>
                        </tr>
                      )}
                      <tr>
                        <th scope="row">{t('seller_detail.location')}</th>
                        <td>{listing.city}{listing.city && listing.location ? ', ' : ''}{listing.location}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="sticky-sidebar mt-3">
                <div className="seller-description-box">
                  <h4 className="mb-3">{t('seller_detail.features')}</h4>

                  {/* Equipment details from structured data */}
                  {listing.equipment && (
                    <table className="table table-sm table-striped mb-3">
                      <tbody>
                        {listing.equipment.isMetallic && (
                          <tr><th>{t('seller_detail.eq_metallic')}</th><td>{listing.equipment.isMetallic}</td></tr>
                        )}
                        {listing.equipment.interiorMaterial && (
                          <tr><th>{t('seller_detail.eq_interior_material')}</th><td>{listing.equipment.interiorMaterial}</td></tr>
                        )}
                        {listing.equipment.interiorColor && (
                          <tr><th>{t('seller_detail.eq_interior_color')}</th><td>{listing.equipment.interiorColor}</td></tr>
                        )}
                        {listing.equipment.cruiseControl && (
                          <tr><th>{t('seller_detail.eq_cruise_control')}</th><td>{listing.equipment.cruiseControl}</td></tr>
                        )}
                        {listing.equipment.airbags && (
                          <tr><th>{t('seller_detail.eq_airbags')}</th><td>{listing.equipment.airbags}</td></tr>
                        )}
                        {listing.equipment.headlights && (
                          <tr><th>{t('seller_detail.eq_headlights')}</th><td>{listing.equipment.headlights}</td></tr>
                        )}
                        {listing.equipment.airConditioning && (
                          <tr><th>{t('seller_detail.eq_air_conditioning')}</th><td>{listing.equipment.airConditioning}</td></tr>
                        )}
                      </tbody>
                    </table>
                  )}

                  {/* Flat features list */}
                  {listing.features && listing.features.length > 0 ? (
                    <div className="features-grid">
                      {listing.features.map((feature, index) => (
                        <div key={index} className="feature-item">
                          {getFeatureLabel(feature)} <span className="check">✔</span>
                        </div>
                      ))}
                    </div>
                  ) : !listing.equipment ? (
                    <p className="text-muted mb-0">{t('seller_detail.no_features')}</p>
                  ) : null}
                </div>
              </div>
              <div className="sticky-sidebar mt-3">
                <div className="seller-description-box">
                  <h4 className="mb-3">
                    {t('seller_detail.vehicle_description')}
                  </h4>
                  <p>{listing.title}</p>
                  <strong>{t('seller_detail.standard_equipment')}</strong>
                  <div>
                    <p>{listing.description}</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-4">
              <div className="sticky-sidebar mb-3">
                <div className="price_sec mb-4">
                  <h3>PKR {Number(listing.price || 0).toLocaleString()}</h3>
                  <span>
                    {t('seller_detail.managed_by')} <strong> AutoPlace </strong>
                  </span>
                </div>
                <h4>
                  {listing.make} {listing.model}
                </h4>
                <p>{listing.location}</p>
                <div className="d-flex gap-3">
                  <button
                    className="btn btn-custom w-100"
                    onClick={handleStartChat}
                    disabled={loading || startingChat}
                  >
                    {t('seller_detail.contact_seller')}
                  </button>
                  <button
                    className="btn btn-custom-outline w-100 d-flex justify-content-center align-items-center"
                    onClick={handleStartChat}
                    disabled={loading || startingChat}
                  >
                    {startingChat ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        {t('seller_detail.starting')}
                      </>
                    ) : (
                      <>
                        <i className="fas fa-share me-2"></i> {t('seller_detail.message')}
                      </>
                    )}
                  </button>
                </div>
              </div>
              <div className="sticky-sidebar ">
                <button
                  className="btn btn-whatsapp w-100"
                  onClick={handleStartChat}
                  disabled={loading || startingChat}
                >
                  <i className="fa-brands fa-whatsapp"></i> {t('seller_detail.start_chat')}
                </button>
              </div>
            </div>
          </div>
          <div className="similar_ads">
            <h2>{t('seller_detail.similar_ads')}</h2>

            {loadingSimilar ? (
              <div className="text-center py-4">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">
                    {t('seller_detail.loading_similar')}
                  </span>
                </div>
              </div>
            ) : similarListings.length === 0 ? (
              <div className="text-center py-4">
                <p className="text-muted">{t('seller_detail.no_similar')}</p>
              </div>
            ) : (
              <div className="row">
                {similarListings.map((similarListing: any) => {
                  const primaryPhoto =
                    similarListing.thumbnailUrl ||
                    similarListing.image ||
                    '/images/placeholder.jpg';

                  const listingTitle =
                    similarListing.title ||
                    `${similarListing.year || ''} ${similarListing.make || ''} ${similarListing.model || ''}`.trim() ||
                    t('seller_detail.car_listing');

                  return (
                    <div
                      key={similarListing.id}
                      className="col-lg-3 col-md-6 col-12"
                    >
                      <Link
                        className="similar_card"
                        to={`/seller-detail/${similarListing.id}`}
                      >
                        <div className="similar-card position-relative">
                          <img
                            src={primaryPhoto}
                            className="similar-img"
                            alt={listingTitle}
                          />
                          <Link
                            className="link"
                            to={`/seller-detail/${similarListing.id}`}
                            onClick={e => e.stopPropagation()}
                          >
                            {listingTitle}
                          </Link>
                          <p className="price mb-1">
                            {formatPrice(similarListing.price || 0)}
                          </p>
                          <p className="location mb-1">
                            {similarListing.location ||
                              t('seller_detail.location_not_specified')}
                          </p>
                        </div>
                      </Link>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}

export default SellerDetail;
